<template>
  <div v-if="isLogicOnly">
    <div class="w-full h-screen flex items-center justify-center">
      <div class="flex flex-col items-center space-y-4">
        <myc-spinner />
        <span class="text-cool-gray-100 text-lg font-semibold">
          {{ $t('common.please-wait') }}
        </span>
      </div>
    </div>
  </div>
  <div
    v-else
    :class="[isRegister && 'h-[calc(100vh-92px)]', !isRegister && 'h-full']"
    class="w-min-screen h-min-screen bg-warm-gray-10"
  >
    <!-- common header for server creation layout (hidden on the provisioning/success screen — it would otherwise render as an empty white bar) -->
    <div
      v-if="!isServerCreated"
      class="w-full bg-white border-b border-[#EBEBEB] px-6 py-5 rounded-b-lg"
    >
      <div class="mx-auto max-w-[1200px]">
        <div
          v-if="!isLoggedIn"
          class="hidden sm:block hover:underline mt-2 text-linkblue mb-4"
        >
          <router-link to="/account/signin">
            Already have an account?
          </router-link>
        </div>

        <div
          v-if="showStepIndicator"
          class="w-full"
        >
          <!-- Progress bars -->
          <div class="flex gap-1 mb-3">
            <div
              v-for="step in wizardSteps"
              :key="'bar-' + step.name"
              class="flex-1 h-1 rounded-full"
              :class="{
                'bg-cool-gray-100': step.status === 'done',
                'bg-green-100': step.status === 'active',
                'bg-[#f5f5f5]': step.status === 'upcoming',
              }"
            />
          </div>
          <!-- Step labels -->
          <div class="flex">
            <div
              v-for="step in wizardSteps"
              :key="'label-' + step.name"
              class="flex-1 flex items-center space-x-2"
              :class="step.status === 'done' && 'cursor-pointer hover:opacity-70'"
              @click="onStepClick(step.name)"
            >
              <!-- Done: black filled circle with checkmark -->
              <div
                v-if="step.status === 'done'"
                class="w-4 h-4 rounded-full bg-cool-gray-100 flex items-center justify-center shrink-0"
              >
                <svg
                  class="w-2.5 h-2.5 text-white"
                  viewBox="0 0 12 12"
                  fill="none"
                >
                  <path
                    d="M2.5 6L5 8.5L9.5 4"
                    stroke="currentColor"
                    stroke-width="1.5"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </div>
              <!-- Active: spinning dashed green circle -->
              <div
                v-else-if="step.status === 'active'"
                class="w-4 h-4 rounded-full border-2 border-dashed border-green-100 shrink-0 animate-spin-slow"
              />
              <!-- Upcoming: dashed gray circle -->
              <div
                v-else
                class="w-4 h-4 rounded-full border-2 border-dashed border-cool-gray-30 shrink-0"
              />
              <!-- Label -->
              <span
                class="text-xs whitespace-nowrap"
                :class="{
                  'text-cool-gray-100 font-medium': step.status === 'done',
                  'text-green-100 font-medium': step.status === 'active',
                  'text-cool-gray-50': step.status === 'upcoming',
                }"
              >
                {{ step.label | nt }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
    <!-- common header for server creation layout -->

    <div
      :class="[isRegister && 'justify-center', !isRegister && 'pb-[164px]']"
      class="h-full mx-auto max-w-[1200px] px-6"
    >
      <!-- wizard steps: content + sidebar layout -->
      <div
        v-if="showStepIndicator"
        class="flex gap-6"
      >
        <div class="flex-1 min-w-0 overflow-hidden">
          <transition
            name="fade"
            mode="out-in"
          >
            <router-view />
          </transition>
        </div>
        <div class="w-80 shrink-0 hidden sm:block mt-4">
          <order-summary />
        </div>
      </div>
      <!-- non-wizard pages (paymentDetails, serverCreated, preloginRegister) -->
      <div
        v-else
        class="flex flex-col items-center"
      >
        <transition
          name="fade"
          mode="out-in"
        >
          <router-view />
        </transition>
      </div>

      <!-- common server creation floating footer -->
      <server-creation-footer
        :is-next-disabled="isNextDisabled"
        :has-prev="hasPrev"
        :hide-footer="hideFooter"
        :hide-next="hideNext"
        @next="onNext"
        @back="onBack"
      />
      <!-- common server creation floating footer -->
    </div>
  </div>
</template>

<script>
// externals
import { storeToRefs } from 'pinia';

// internals
import {
  create,
  getConfigurablesPublic,
  getCurrentPrice,
  getOne,
  getPlansPublic,
  update,
} from '@/modules/deployment/DeploymentService';
import { useAuthStore, useCompanyStore, useServerCreationStore } from '@/stores';
import { featureCodeMatches } from '@/stores/server-creation';
import { GtmEvent, pushGtmEvent } from '@/utils/gtm';
import { getIdempotencyKey } from '@/utils/idempotency';
import { AnalyticsMixin } from '@/utils/mixins';
import { serverCreationSteps } from '@/utils/server-creation/config';
import {
  CloudProvider,
  Currency,
  FeatureTypes,
  PaymentPeriod,
} from '@/utils/server-creation/types/server-creation-types';

// components
import mycSpinner from '@/components/common/myc-spinner.vue';
import OrderSummary from './containers/OrderSummary.vue';
import ServerCreationFooter from './containers/ServerCreationFooter.vue';

export default {
  name: 'ServerBuilder',
  components: {
    OrderSummary,
    ServerCreationFooter,
    mycSpinner,
  },
  mixins: [AnalyticsMixin],
  setup() {
    // init stores
    const companyStore = useCompanyStore();
    const serverCreationStore = useServerCreationStore();
    const authStore = useAuthStore();

    // expose reactive refs
    const { selectedCompany } = storeToRefs(companyStore);
    const {
      serverCreationState,
      plans,
      configurables,
    } = storeToRefs(serverCreationStore);
    const { isLoggedIn } = storeToRefs(authStore);

    // expose methods
    const {
      setServerCreationState,
      getServerCreationPayload,
      resetServerCreationState,
      setPlans,
      setConfigurables,
      setLoading,
    } = serverCreationStore;

    // final expose
    return {
      setLoading,
      selectedCompany,
      configurables,
      setConfigurables,
      plans,
      setPlans,
      serverCreationState,
      setServerCreationState,
      resetServerCreationState,
      getServerCreationPayload,
      isLoggedIn,
    };
  },
  data() {
    return {
      price: '0',
      currency: Currency.USD,
      period: PaymentPeriod.MONTH,
      idempotencyKey: '',
      isEdit: false,
      action: 'Create',
      preventClose: false,
      hidePayment: false,
      isNextDisabled: false,
      isLogicOnly: false, // used to prevent next button click,
      lastPriceCtx: null,
    };
  },
  computed: {
    isPrelogin() {
      return this.$route.meta.prelogin || false;
    },
    isRegister() {
      return this.$route.name === 'preloginRegister';
    },
    hideNext() {
      return serverCreationSteps[this.$route.name].hideNext || false;
    },
    hasPrev() {
      return serverCreationSteps[this.$route.name].prev !== null || false;
    },
    hideFooter() {
      return serverCreationSteps[this.$route.name].hideFooter || false;
    },
    isServerCreated() {
      return this.$route.name === 'serverCreated';
    },
    isCloseAllowed() {
      return !this.isServerCreated && !this.isRegister;
    },
    wizardSteps() {
      const firstStep = this.isPrelogin ? 'preloginChoosePlan' : 'choosePlan';
      const steps = [];
      let current = firstStep;
      while (current && serverCreationSteps[current] && !serverCreationSteps[current].hideFooter) {
        const step = serverCreationSteps[current];
        if (step.label) {
          steps.push({ name: current, label: step.label });
        }
        current = step.next;
      }
      const currentIndex = steps.findIndex((s) => s.name === this.$route.name);
      return steps.map((step, index) => {
        let status = 'upcoming';
        if (index < currentIndex) status = 'done';
        else if (index === currentIndex) status = 'active';
        return { ...step, status };
      });
    },
    showStepIndicator() {
      return this.wizardSteps.some((s) => s.name === this.$route.name);
    },
  },
  watch: {
    // watch for changes in subscription data to recalculate price
    'serverCreationState.data.subscription': {
      handler() {
        this.calculatePrice();
      },
      deep: true,
    },
  },
  async mounted() {
    if (this.$route.name === 'preloginChoosePlan') {
      this.persistUtmTags();

      if (this.$route.query.cly_id) {
        this.setDeviceId('landing', this.$route.query.cly_id);
      }
    }
    // if (this.$route.query.draftId) {
    //   this.serverCreationState.data.subscription.draftId = this.$route.query.draftId;
    //   this.getDraft();
    // }
    this.startTimedEvent(this.$route.params.id ? 'serverUpdate' : 'serverCreate');
    this.startTimedEvent(this.$route.params.id ? `serverUpdate${serverCreationSteps[this.$route.name].prefix}` : `serverCreate${serverCreationSteps[this.$route.name].prefix}`);
    // Mark edit mode up-front so the rest of the lifecycle (children, watchers) sees it.
    if (this.$route.params.id) {
      this.isEdit = true;
      this.action = 'Update';
    }

    // Fetch configurables, plans, and (if edit) deployment detail in parallel.
    // processRawDeploymentData depends on configurables.data.providers being populated,
    // so configurables/plans MUST be applied to the store before we process the detail
    // response — otherwise the first edit-mode mount throws "Cannot find provider ID".
    const detailPromise = this.isEdit
      ? getOne({ _id: this.$route.params.id })
      : Promise.resolve(null);
    const [detailResult, configsResult, plansResult] = await Promise.allSettled([
      detailPromise,
      getConfigurablesPublic({ providerCode: CloudProvider.GCP }),
      getPlansPublic(this.selectedCompany),
    ]);

    if (configsResult.status === 'fulfilled') {
      this.setConfigurables(configsResult.value.value.payload);
    }

    if (plansResult.status === 'fulfilled') {
      this.setPlans(plansResult.value.value.payload);
    }

    // Now safe to process the deployment detail — configurables/plans are loaded.
    if (this.isEdit) {
      const detailResponse = detailResult.status === 'fulfilled' ? detailResult.value : null;
      if (detailResponse && detailResponse.isOk()) {
        try {
          this.syncServerCreationState(this.processRawDeploymentData(detailResponse));
        } catch (err) {
          this.$clyNotify({ message: 'An error occurred while processing server.', sticky: false, type: 'error' });
          this.$router.push({
            name: 'dashboardRoot',
            params: {
              cid: this.$route.params.cid,
              pid: this.$route.params.pid,
            },
          });
        }
      } else {
        this.$clyNotify({ message: 'Server detail could not be loaded.', sticky: false, type: 'error' });
        this.$router.push({
          name: 'dashboardRoot',
          params: {
            cid: this.$route.params.cid,
            pid: this.$route.params.pid,
          },
        });
      }
    }

    this.setLoading(false);

    // init server creation defaults if there is no data to restore
    // if ((!this.serverCreationState.data.inProgress && !this.isEdit) || !this.serverCreationState.data.subscription.draftId) {
    if (!this.serverCreationState.data.inProgress && !this.isEdit) {
      this.resetServerCreationState();
    }

    // If user direct-lands on a middle step (e.g. /choose-add-ons) without going
    // through the plan picker, the wizard has no plan in state — downstream steps
    // render empty/stale. Send them back to the first step in that case. Same when
    // persisted state has a stale planCode that no longer matches any plan.
    // Handles both logged-in and prelogin wizard variants by reading route meta.
    const isPrelogin = !!this.$route.meta?.prelogin;
    const firstStepName = isPrelogin ? 'preloginChoosePlan' : 'choosePlan';
    if (!this.isEdit && this.$route.name !== firstStepName) {
      const { planCode } = this.serverCreationState.data.subscription;
      const hasMatchingPlan = planCode && this.plans.data.some((p) => p.code === planCode);
      if (!hasMatchingPlan) {
        const redirectTarget = { name: firstStepName };
        if (!isPrelogin) {
          redirectTarget.params = {
            cid: this.$route.params.cid,
            pid: this.$route.params.pid,
          };
        }
        this.$router.replace(redirectTarget);
        return;
      }
    }

    this.calculatePrice();
    this.setServerCreationState(['inProgress'], true);
    this.idempotencyKey = getIdempotencyKey();

    this.setServerCreationState(['pricing', 'isDiscountApplied'], false);

    if (this.$route.query.continue) {
      this.isLogicOnly = true;
      await this.submit(this.serverCreationState.data.pricing.total === '0', this.isEdit);
      localStorage.removeItem('complete_setup');
    } else {
      this.isLogicOnly = false;
    }
  },
  created() {
    this.setLoading(true);

    // Always start the wizard with a clean slate. Selections from a previous wizard
    // run (cancelled, completed, or abandoned via browser back) otherwise leak into
    // the new run, because the Pinia store survives in memory across in-app
    // navigations. Runs in `created` — before children mount — so ChoosePlan's
    // `initialPlanCode` read isn't poisoned by stale values.
    //
    // For edit mode, the subsequent `syncServerCreationState` call (in mounted)
    // overwrites these defaults with the actual deployment data from the API.
    // For create mode, mounted's `resetServerCreationState` fills in defaults
    // once plans/configurables are fetched.
    this.setServerCreationState(['inProgress'], false);
    this.setServerCreationState(['isLoading'], true);
    this.setServerCreationState(['isActiveServerFree'], false);
    this.setServerCreationState(['serverId'], '');
    this.setServerCreationState(['activePaymentPeriod'], 'month');
    this.setServerCreationState(['subscription', '_id'], '');
    this.setServerCreationState(['subscription', 'draftId'], '');
    this.setServerCreationState(['subscription', 'planCode'], '');
    this.setServerCreationState(['subscription', 'planId'], '');
    this.setServerCreationState(['subscription', 'providerId'], '');
    this.setServerCreationState(['subscription', 'planConfig'], {
      region: '', features: [], bundles: [], size: '', provider: 'gcp',
    });
    this.setServerCreationState(['subscription', 'specialFeatures'], { domain: '', backup: '', retention: '' });
    this.setServerCreationState(['supportLevel'], 'standard');
    this.setServerCreationState(['oneTimeServices'], []);
    this.setServerCreationState(['name'], '');
    // Pricing snapshot from a prior run would otherwise paint the UI with a stale
    // total until the new calculatePrice response lands, and `_id` from a cancelled
    // upgrade would make the backend treat this fresh wizard as a modify-existing
    // request and return wrong numbers.
    this.setServerCreationState(['pricing'], {
      total: 0,
      currency: 'USD',
      isDiscountApplied: false,
      discountCode: '',
      discountedTotal: {
        subtotal: 0, tax: 0, total: 0, discount: 0, credit: 0, balance: 0, currency: 'USD',
      },
    });
    this.setServerCreationState(['provisionSummary'], {
      dataToVendor: {
        company: '', deployment: '', paymentMethod: '', subscription: '',
      },
      items: [],
      total: { amount: '0', currency: 'USD' },
    });
  },
  beforeRouteLeave(to, from, next) {
    // If user navigates anywhere outside the wizard, drop volatile flags so a
    // future re-entry starts clean. Inter-step navigation does NOT trigger this
    // hook because ServerBuilder remains mounted.
    const wizardRoutes = Object.keys(serverCreationSteps);
    if (!to || !wizardRoutes.includes(to.name)) {
      this.setServerCreationState(['inProgress'], false);
      this.setServerCreationState(['isLoading'], false);
      this.setServerCreationState(['serverId'], '');
      this.idempotencyKey = '';
    }
    next();
  },
  methods: {
    persistUtmTags() {
      const utmTags = [];

      if (this.$route.query.utm_source) {
        utmTags.push({ key: 'utm_source', value: this.$route.query.utm_source });
      }

      if (this.$route.query.utm_medium) {
        utmTags.push({ key: 'utm_medium', value: this.$route.query.utm_medium });
      }

      if (this.$route.query.utm_campaign) {
        utmTags.push({ key: 'utm_campaign', value: this.$route.query.utm_campaign });
      }

      if (utmTags.length) {
        localStorage.setItem('utm_tags', JSON.stringify(utmTags));
      }
    },
    syncServerCreationState(payload) {
      this.setServerCreationState(['name'], payload.deployment.name);
      this.setServerCreationState(['isActiveServerFree'], payload.deployment.flags.free);
      this.setServerCreationState(['activePaymentPeriod'], payload.deployment.subscription.payment.term.cycle);
      this.setServerCreationState(['subscription', 'planCode'], payload.deployment.subscription.planCode);
      this.setServerCreationState(['subscription', 'planId'], payload.deployment.subscription.plan);
      this.setServerCreationState(['subscription', 'planConfig'], payload.planConfig);
      this.setServerCreationState(['subscription', 'providerId'], payload.providerId);
      this.setServerCreationState(['subscription', 'specialFeatures', 'domain'], payload.domainFeature);
      this.setServerCreationState(['subscription', 'specialFeatures', 'retention'], payload.retentionFeature);
      this.setServerCreationState(['subscription', 'specialFeatures', 'backup'], payload.backupFeature);
      this.setServerCreationState(['supportLevel'], payload.supportLevel);
      this.setServerCreationState(['oneTimeServices'], payload.oneTimeServiceCodes);
    },
    processRawDeploymentData(rawDeploymentData) {
      try {
        const deployment = rawDeploymentData?.value?.payload?.deployment;

        if (!deployment) {
          throw new Error('Cannot find deployment data');
        }

        const provider = this.configurables?.data?.providers?.find((p) => p.code === deployment.provider);
        const providerId = provider?._id;

        if (!providerId) {
          throw new Error('Cannot find provider ID');
        }

        const { subscription } = deployment;
        const planConfig = subscription?.planConfig;
        if (!planConfig) {
          throw new Error('Cannot find plan config');
        }

        const features = planConfig.features || [];
        const bundles = planConfig.bundles || [];
        const domainFeature = features.find((feature) => feature.type === FeatureTypes.DOMAIN)?._id;
        const retentionFeature = features.find((feature) => feature.type === FeatureTypes.RETENTION)?._id;
        const backupFeature = features.find((feature) => feature.type === FeatureTypes.BACKUP)?._id;
        // Support level lives in its own store field (`supportLevel`), not in planConfig.features.
        // Without extracting it here, the existing support feature ID would leak into the price
        // request and the backend would re-add the support cost on top of the UI-displayed total.
        const supportFeature = features.find((feature) => feature.type === FeatureTypes.SUPPORT);
        const supportLevel = featureCodeMatches(supportFeature?.code, 'support-dedicated') ? 'dedicated' : 'standard';
        // One-time services are tracked separately by code, not feature ID, in `oneTimeServices`.
        const oneTimeServiceCodes = features
          .filter((feature) => feature && feature.type === FeatureTypes.ONE_TIME_SERVICE && feature.code)
          .map((feature) => feature.code);
        // Exclude domain/retention/backup/support/one-time from planConfig.features – they live in dedicated store fields.
        const filteredFeatures = Array.isArray(features)
          ? features
            .filter((feature) => feature
              && feature.type !== FeatureTypes.DOMAIN
              && feature.type !== FeatureTypes.RETENTION
              && feature.type !== FeatureTypes.BACKUP
              && feature.type !== FeatureTypes.SUPPORT
              && feature.type !== FeatureTypes.ONE_TIME_SERVICE)
            .map((feature) => (typeof feature === 'object' && feature._id ? feature._id : feature))
            .filter(Boolean)
          : [];
        const bundleIds = Array.isArray(bundles)
          ? bundles.map((b) => (typeof b === 'object' && b._id ? b._id : b)).filter(Boolean)
          : [];

        return {
          planConfig: {
            region: planConfig.region,
            size: planConfig.size,
            features: filteredFeatures,
            bundles: bundleIds.length ? bundleIds : undefined,
            provider: deployment.provider,
          },
          providerId,
          domainFeature,
          retentionFeature,
          backupFeature,
          supportLevel,
          oneTimeServiceCodes,
          deployment,
        };
      } catch (error) {
        throw new Error('Error while processing deployment data.');
      }
    },
    close() {
      // since user close the server creation wizard with the close button
      // we won't open this view if user tries to navigate back
      this.setServerCreationState(['inProgress'], false);

      // redirect to the dashboard
      this.$router.push({
        name: 'dashboardRoot',
        params: {
          cid: this.$route.params.cid,
          pid: this.$route.params.pid,
        },
      });
    },
    validateServerName() {
      // Reject whitespace-only / too-short names. ChooseRegion's v-model.trim
      // already strips leading/trailing whitespace, but this guard also catches
      // names that arrived through any other path (e.g. persisted state, edit hydration).
      const trimmedName = (this.serverCreationState.data.name || '').trim();
      if (trimmedName.length < 2) {
        this.$clyNotify({
          message: this.$t('mycountly.dashboard.deployments.invalid-server-name'),
          sticky: false,
          type: 'error',
        });
        return false;
      }
      return true;
    },
    async submit(isFree, isEdit) {
      // Defense-in-depth: same check also runs on the chooseRegion step's Next click.
      if (!this.validateServerName()) {
        return;
      }

      let response;

      const requestPayload = this.getServerCreationPayload(isFree, this.$route.params.cid, this.$route.params.pid, isEdit);

      // CREATE_CASES
      // FREE ----> No payment required
      // PAID ----> Payment required

      // EDIT_CASES
      // FREE ----> FREE ----> No payment required
      // PAID ----> PAID ----> No payment required
      // FREE ----> PAID ----> Payment required
      const freeToFree = isFree && this.serverCreationState.data.isActiveServerFree;
      const paidToPaid = !this.serverCreationState.data.isActiveServerFree && !isFree;
      const freeToPaid = !isFree && this.serverCreationState.data.isActiveServerFree;

      if (isEdit) {
        response = await update(this.$route.params.id, requestPayload, freeToPaid);
      } else {
        const idempotencyKey = this.idempotencyKey || getIdempotencyKey();
        this.idempotencyKey = idempotencyKey;
        response = await create(requestPayload, !isFree, idempotencyKey);
      }

      if (response.isOk()) {
        let nextRoute;

        // decide the next route based on the payment status
        if (isEdit) {
          if (freeToFree || paidToPaid) {
            nextRoute = { name: 'serverCreated' };
          } else {
            nextRoute = { name: 'paymentDetails' };
          }
          nextRoute.params = { id: this.$route.params.id };
        } else {
          const isBypassed = response.value.payload.flags?.free === true;
          nextRoute = { name: (isFree || isBypassed) ? 'serverCreated' : 'paymentDetails' };
        }

        // set server id to store
        this.setServerCreationState(['serverId'], response.value.payload._id);

        if (!isFree) {
          // set provision summary to store if it's a paid deployment, bcz we'll need it in paddle step
          this.setServerCreationState(['provisionSummary'], response.value.payload.provisionSummary);
        }
        this.startTimedEvent(this.isEdit ? 'serverUpdate' : 'serverCreate');

        this.isNextDisabled = false;
        this.isLogicOnly = false;
        this.$router.push(nextRoute);
      } else {
        this.isNextDisabled = false;
        this.isLogicOnly = false;
        this.$clyNotify({ message: this.$t(response.error.message), sticky: false, type: 'error' });

        this.$router.push({
          name: 'dashboardRoot',
          params: {
            cid: this.$route.params.cid,
            pid: this.$route.params.pid,
          },
        });
      }
    },
    async onNext() {
      window.scrollTo({
        top: 0,
        behavior: 'smooth',
      });

      // Validate server name on the step where it's entered, so the user sees the
      // error here instead of after navigating several steps forward.
      if (this.$route.name === 'chooseRegion' || this.$route.name === 'preloginChooseRegion') {
        if (!this.validateServerName()) {
          return;
        }
      }

      this.pushGtmStepEvent(this.$route.name);

      if (!this.isLoggedIn) {
        if (this.$route.name === 'preloginChooseRegion') {
          this.$router.push({ name: 'preloginRegister' });
        } else if (this.$route.name === 'preloginChoosePlan') {
          this.$router.push({ name: 'preloginChooseRegion' });
        }
        return;
      }

      // this.draftUpdate();
      // if we're on the last step before server creation
      if (this.$route.name === 'chooseExtras') {
        const key = `server${this.action}${serverCreationSteps[this.$route.name].prefix}`;
        this.endTimedEvent(key, {
          key,
          segmentation: this.getSegment('chooseExtras'),
        });
        this.endTimedEvent(`server${this.action}`);
        this.isNextDisabled = true;
        await this.submit(this.serverCreationState.data.pricing.total === '0', this.isEdit);
      } else {
        const nextRoute = { name: serverCreationSteps[this.$route.name].next };
        if (this.isEdit) {
          nextRoute.params = { id: this.$route.params.id };
        }
        const key = `server${this.action}${serverCreationSteps[this.$route.name].prefix}`;
        if (this.$route.name === 'choosePlan' || this.$route.name === 'coreFeatures') {
          this.$router.push(nextRoute);
        } else {
          this.endTimedEvent(key, {
            key,
            segmentation: this.getSegment(this.$route.name),
          });
          this.$router.push(nextRoute);
          this.startTimedEvent(`server${this.action}${serverCreationSteps[nextRoute.name].prefix}`);
        }
      }
    },
    onBack() {
      window.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
      // Reset Continue button so a stale disabled state from a previous validation
      // failure on this step does not carry over to the previous step.
      this.isNextDisabled = false;
      const nextRoute = { name: serverCreationSteps[this.$route.name].prev };
      const key = `server${this.action}${serverCreationSteps[this.$route.name].prefix}`;
      if (this.isEdit) {
        nextRoute.params = { id: this.$route.params.id };
      }
      if (this.$route.name === 'chooseRegion' || this.$route.name === 'coreFeatures') {
        this.$router.push(nextRoute);
      } else {
        this.endTimedEvent(key, {
          key,
          segmentation: this.getSegment(this.$route.name),
        });
        this.$router.push(nextRoute);
        this.startTimedEvent(`server${this.action}${serverCreationSteps[nextRoute.name].prefix}`);
      }
    },
    onStepClick(stepName) {
      // Only allow backward navigation: clicked step must be already completed.
      const target = this.wizardSteps.find((s) => s.name === stepName);
      if (!target || target.status !== 'done') return;
      if (stepName === this.$route.name) return;

      window.scrollTo({
        top: 0,
        behavior: 'smooth',
      });

      const nextRoute = { name: stepName };
      if (this.isEdit) {
        nextRoute.params = { id: this.$route.params.id };
      }

      // Mirror onBack timed-event behavior so analytics stay consistent.
      if (this.$route.name === 'chooseRegion' || this.$route.name === 'coreFeatures') {
        this.$router.push(nextRoute);
      } else {
        const key = `server${this.action}${serverCreationSteps[this.$route.name].prefix}`;
        this.endTimedEvent(key, {
          key,
          segmentation: this.getSegment(this.$route.name),
        });
        this.$router.push(nextRoute);
        this.startTimedEvent(`server${this.action}${serverCreationSteps[nextRoute.name].prefix}`);
      }
    },
    // Google Ads funnel-step events (GTM dataLayer). Fired when a wizard step
    // is completed via Continue; edits of existing servers are not part of the
    // acquisition funnel, so they are skipped.
    pushGtmStepEvent(routeName) {
      if (this.isEdit) {
        return;
      }

      const stepEvents = {
        choosePlan: GtmEvent.PLAN_SELECTED,
        preloginChoosePlan: GtmEvent.PLAN_SELECTED,
        chooseRegion: GtmEvent.CONFIG_COMPLETED,
        preloginChooseRegion: GtmEvent.CONFIG_COMPLETED,
        coreFeatures: GtmEvent.INCLUDED_VIEWED,
        chooseAddOns: GtmEvent.CAPABILITIES_SELECTED,
        chooseExtras: GtmEvent.EXTRAS_VIEWED,
      };
      const eventName = stepEvents[routeName];
      if (!eventName) {
        return;
      }

      const subscription = this.serverCreationState.data.subscription || {};
      const payload = { account_id: useAuthStore().loggedInUser?._id };

      // Payload enrichment must never break wizard navigation.
      try {
        if (eventName === GtmEvent.PLAN_SELECTED) {
          const plan = this.plans.data.find((p) => p._id === subscription.planId);
          payload.tier = subscription.planCode;
          payload.plan_name = plan?.name;
        } else if (eventName === GtmEvent.CONFIG_COMPLETED) {
          payload.region = this.getName('regions', subscription.planConfig?.region)?.name;
          payload.retention = this.getName('features', subscription.specialFeatures?.retention)?.name;
        } else if (eventName === GtmEvent.CAPABILITIES_SELECTED) {
          payload.addons = (subscription.planConfig?.features || [])
            .map((featureId) => this.getName('features', featureId)?.code)
            .filter(Boolean);
        }
      } catch (e) {
        console.warn('GTM step payload could not be built:', e.message);
      }

      pushGtmEvent(eventName, payload);
    },
    getSegment(segmentName) {
      switch (segmentName) {
        case 'chooseRegion': {
          const region = this.getName('regions', this.serverCreationState.data.subscription.planConfig.region);
          const planObj = this.plans.data.find((p) => p._id === this.serverCreationState.data.subscription.planId);
          return {
            Plan: planObj?.name || '',
            Region: region?.name || '',
          };
        }
        case 'chooseAddOns': {
          const featureIds = this.serverCreationState.data.subscription.planConfig.features || [];
          const featureNames = featureIds
            .map((featureId) => this.getName('features', featureId)?.code)
            .filter((v) => !!v);
          const bundleIds = this.serverCreationState.data.subscription.planConfig.bundles || [];
          const bundleNames = bundleIds
            .map((bundleId) => {
              const b = (this.configurables.data.bundles || []).find(
                (bundle) => bundle._id === bundleId,
              );
              return b ? (b.code || b.name) : null;
            })
            .filter(Boolean);
          return {
            'Feature Add-Ons': featureNames,
            ...(bundleNames.length > 0 ? { Bundles: bundleNames } : {}),
          };
        }
        default: {
          const retentionFeature = this.getName('features', this.serverCreationState.data.subscription.specialFeatures.retention);
          const domainFeature = this.getName('features', this.serverCreationState.data.subscription.specialFeatures.domain);
          const backupFeature = this.getName('features', this.serverCreationState.data.subscription.specialFeatures.backup);

          const retention = retentionFeature?.name || '';
          const domainName = domainFeature?.name || '';
          const backupIdOrCode = backupFeature?.code || backupFeature?.name || '';
          return {
            'Data Retention': retention,
            'Domain SSL': domainName.includes('custom') ? 'Custom' : 'Default',
            'Hourly Backup': typeof backupIdOrCode === 'string' && backupIdOrCode.toLowerCase().includes('hourly'),
          };
        }
      }
    },
    // async getDraft() {
    //   const resp = await getDraft();
    //   if (resp.value.code === 200) {
    //     const { draft } = resp.value.payload;
    //     this.setDraftConfig(draft);
    //   }
    // },
    // setDraftConfig(draft) {
    //   this.serverCreationState.data = {
    //     inProgress: this.serverCreationState.data.inProgress,
    //     isActiveServerFree: this.serverCreationState.data.isActiveServerFree,
    //     name: draft.name,
    //     subscription: {
    //       _id: '',
    //       planCode: draft.step1.planCode,
    //       planId: draft.step1.planId,
    //       planConfig: {
    //         region: draft.step2.region,
    //         features: draft.step3.features,
    //         size: draft.size,
    //         provider: generalAppVariables.DEFAULT_PROVIDER,
    //       },
    //       specialFeatures: {
    //         domain: draft.step4.domain,
    //         backup: draft.step4.backUp,
    //         retention: draft.step4.retention,
    //       },
    //       providerId: this.serverCreationState.data.subscription.providerId,
    //     },
    //     activePaymentPeriod: draft.activePaymentPeriod,
    //     pricing: {
    //       total: draft.total,
    //       currency: draft.currency,
    //     },
    //     isLoading: false,
    //   };
    //   if (draft.lastStepUrl !== 'choosePlan') {
    //     this.$router.push({
    //       name: draft.lastStepUrl,
    //       params: {
    //         cid: this.$route.params.cid,
    //         pid: this.$route.params.pid,
    //       },
    //       query: {
    //         draftId: this.$route.query.draftId,
    //       },
    //     });
    //   }
    // },
    getName(category, id) {
      return this.configurables.data[category].find((f) => f._id === id);
    },
    // async draftUpdate() {
    //   const draft = this.serverCreationState.data;
    //   const payload = { ...draft, lastStepUrl: this.$route.name };
    //   if (this.serverCreationState.data.draftId) {
    //     await updateDraft(payload);
    //   } else {
    //     const obj = await createDraft(payload);
    //     this.serverCreationState.data.subscription.draftId = obj?.value?.payload?.draft._id;
    //   }
    // },
    onSwitchPeriod(period) {
      this.setServerCreationState(['activePaymentPeriod'], period);
      this.calculatePrice();
    },
    async calculatePrice() {
      if (this.serverCreationState.data.isLoading) return;

      const {
        features: addOnIdsArr,
        bundles: bundleIdsArr,
      } = this.serverCreationState.data.subscription.planConfig;
      const {
        domain: domainId,
        retention: retentionId,
      } = this.serverCreationState.data.subscription.specialFeatures;
      const {
        providerId,
        planId,
      } = this.serverCreationState.data.subscription;

      // Don't fire pricing until required fields are resolved. The subscription deep-watcher
      // re-triggers this method as soon as ChooseRegion / reset logic fills the gaps, so a
      // skip here just defers the request — it does not drop it.
      const subPlanConfig = this.serverCreationState.data.subscription.planConfig;
      if (!planId || !providerId || !subPlanConfig.region || !subPlanConfig.size) {
        return;
      }

      // Drop falsy entries — domain/retention/backup ids can be '' when the matching feature
      // isn't present in configurables, and the backend rejects empty-string ids in the array.
      const featuresIdsArr = [...(addOnIdsArr || []), domainId, retentionId].filter(Boolean);

      const planConfig = {
        features: featuresIdsArr,
        region: this.serverCreationState.data.subscription.planConfig.region,
        size: this.serverCreationState.data.subscription.planConfig.size,
        provider: providerId,
        ...(bundleIdsArr && bundleIdsArr.length > 0 ? { bundles: bundleIdsArr } : {}),
      };

      const priceCtx = {
        planCode: this.serverCreationState.data.subscription.planCode,
        planId,
        size: this.serverCreationState.data.subscription.planConfig.size,
        region: this.serverCreationState.data.subscription.planConfig.region,
        period: this.serverCreationState.data.activePaymentPeriod,
        bundles: bundleIdsArr,
      };
      this.lastPriceCtx = priceCtx;

      const priceResponse = await getCurrentPrice({
        _id: this.serverCreationState.data.subscription._id || undefined,
        plan: planId,
        planConfig,
        payment: {
          term: {
            cycle: this.serverCreationState.data.activePaymentPeriod,
          },
        },
      });

      if (!this.lastPriceCtx || priceCtx.planCode !== this.serverCreationState.data.subscription.planCode
          || priceCtx.planId !== this.serverCreationState.data.subscription.planId
          || priceCtx.size !== this.serverCreationState.data.subscription.planConfig.size
          || priceCtx.region !== this.serverCreationState.data.subscription.planConfig.region
          || priceCtx.period !== this.serverCreationState.data.activePaymentPeriod
          || (priceCtx.bundles?.length
            ? JSON.stringify([...(priceCtx.bundles || [])].sort()) !== JSON.stringify([...(this.serverCreationState.data.subscription.planConfig.bundles || [])].sort())
            : false)) {
        return;
      }

      if (priceResponse.isOk()) {
        this.price = priceResponse.value.payload.total.amount;
        this.currency = priceResponse.value.payload.total.currency;
        this.hidePayment = this.price === '0';
        this.setServerCreationState(['pricing', 'total'], priceResponse.value.payload.total.amount);
        this.setServerCreationState(['pricing', 'currency'], priceResponse.value.payload.total.currency);
      } else {
        this.$clyNotify({ message: this.$t('mycountly.dashboard.deployments.price-calculation-failed'), sticky: false, type: 'error' });
      }
    },
  },
};
</script>

<style>
.fade-enter-active, .fade-leave-active {
  transition: opacity 0.5s;
}
.fade-enter, .fade-leave-to /* .fade-leave-active in <2.1.8 */ {
  opacity: 0;
}
.slide-enter-active,
.slide-leave-active {
  transition: all 0.50s ease;
}

.slide-enter-to {
  position: absolute;
  right: 0;
  left: 0;
  opacity: 1;
}

.slide-enter-from {
  position: absolute;
  right: -25%;
  opacity: 0;
}

.slide-leave-to {
  position: absolute;
  left: -25%;
  opacity: 0;
}

.slide-leave-from {
  position: absolute;
  left: 0;
  opacity: 1;
}

@keyframes spin-slow {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.animate-spin-slow {
  animation: spin-slow 3s linear infinite;
}
</style>
